import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Skeleton';

export type ButtonVariant =
  'primary' | 'secondary' | 'tertiary' | 'destructive' | 'inverse' | 'onDark';
export type ButtonSize = 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary: 'border border-line-strong bg-white text-ink hover:bg-surface',
  tertiary: 'bg-transparent text-primary hover:bg-primary-soft',
  destructive: 'border border-danger/40 bg-white text-danger hover:bg-danger-soft',
  inverse: 'bg-ink text-white hover:bg-black',
  // For the dark staff header: an outlined light button, with a light focus ring so it stays visible.
  onDark:
    'border border-white/30 bg-transparent text-white hover:bg-white/10 focus-visible:outline-white',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'min-h-11 px-4 text-sm', // 44 px minimum touch target
  lg: 'min-h-13 px-5 text-base', // 52 px: the one decisive action on a screen
};

/** Class names for anything that must look like a button, for example a router `<Link>`. */
export function buttonClasses(
  options: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean } = {},
): string {
  const { variant = 'secondary', size = 'md', fullWidth = false } = options;
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-control font-semibold no-underline transition-colors',
    'disabled:cursor-not-allowed disabled:bg-line disabled:text-faint disabled:border-transparent',
    VARIANTS[variant],
    SIZES[size],
    fullWidth && 'w-full',
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Shows a spinner, disables the button and sets aria-busy. The label stays for screen readers. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size,
    fullWidth,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonClasses({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
});
